import { useMemo, type Dispatch, type SetStateAction } from "react";
import { addOption, connectField, removeOption, setOptionLabel, toggleProject, updateField, type Draft, type OpenProject } from "../../../lib/formEdits";
import { addRule, addTag, removeRule, removeTag, setRule, setRuleTag } from "../../../lib/formDraftRules";
import type { PanelEdits } from "./FieldSettings";
import type { TagEdits } from "./FormTagsSection";

/** The builder's edits bound to its draft (the pure edits are `lib/formEdits.ts` and `lib/formDraftRules.ts`); each change is a functional
 *  update, so two quick changes never undo each other. */
export function usePanelEdits(setDraft: Dispatch<SetStateAction<Draft>>, uid: string | null, projects: readonly OpenProject[]): PanelEdits {
  return useMemo(() => {
    const on = (fn: (d: Draft, u: string) => Draft) => { if (uid) setDraft((d) => fn(d, uid)); };
    return {
      update: (patch) => on((d, u) => updateField(d, u, patch)),
      connect: (mapping) => on((d, u) => connectField(d, u, mapping, projects)),

      optionLabel: (i, label) => on((d, u) => setOptionLabel(d, u, i, label)),
      addOption: () => on(addOption),
      removeOption: (i) => on((d, u) => removeOption(d, u, i)),
      toggleProject: (p, yes) => on((d, u) => toggleProject(d, u, p, yes)),
    };
  }, [setDraft, uid, projects]);
}
export function useTagEdits(setDraft: Dispatch<SetStateAction<Draft>>): TagEdits {
  return useMemo(() => ({
    add: (list, id) => setDraft((d) => addTag(d, list, id)),
    remove: (list, id) => setDraft((d) => removeTag(d, list, id)),
    addRule: () => setDraft(addRule),
    setRule: (i, patch) => setDraft((d) => setRule(d, i, patch)),
    ruleTag: (i, list, id, yes) => setDraft((d) => setRuleTag(d, i, list, id, yes)),
    removeRule: (i) => setDraft((d) => removeRule(d, i)),
  }), [setDraft]);
}
